import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

// Redux Submission Core & Evaluation Thunks
import { setCurrentPage } from "../../redux/admin/homeworkSubmission.slice";
import { fetchSubmissionsByAssignment, recordSubmissionEvaluation } from "../../redux/admin/homeworkSubmission.thunk";
import { 
  selectHomeworkSubmissions, 
  selectHomeworkSubmissionLoading,
  selectHomeworkSubmissionCurrentPage,
  selectHomeworkSubmissionTotalPages
} from "../../redux/admin/homeworkSubmission.selector";

// Cross-Slice Resolution Roster Hydration (For Student Name Resolution if missing in API response)
import { fetchStudents } from "../../redux/admin/student.thunk";

import toastr from "toastr";
import "toastr/build/toastr.min.css";

/* ─── Spinner Component ─── */
const Spinner = ({ size = 16, color = "#ffffff", thickness = 2 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    style={{ animation: "hms-spin 0.72s linear infinite", flexShrink: 0 }}
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" stroke={color} strokeOpacity="0.22" strokeWidth={thickness} />
    <path d="M12 2a10 10 0 0 1 10 10" stroke={color} strokeWidth={thickness} strokeLinecap="round" />
  </svg>
);

/* ─── Skeleton Shimmer Loader ─── */
const Skeleton = ({ w = "100%", h = 14, r = 6, style: extra = {} }) => (
  <div
    style={{
      width: w, height: h, borderRadius: r,
      background: "linear-gradient(90deg,#f0f0f5 25%,#e8e8ed 50%,#f0f0f5 75%)",
      backgroundSize: "600px 100%",
      animation: "hms-shimmer 1.4s infinite linear",
      ...extra,
    }}
  />
);

const HomeworkSubmissionList = () => {
  const dispatch = useDispatch();
  const limit = 10;

  /* =====================================================
     REDUX SELECTORS
  ====================================================== */
  const submissions = useSelector(selectHomeworkSubmissions) || [];
  const loading = useSelector(selectHomeworkSubmissionLoading);
  const currentPage = useSelector(selectHomeworkSubmissionCurrentPage) || 1;
  const totalPages = useSelector(selectHomeworkSubmissionTotalPages) || 1;

  // Resolving global students array for lookups if the payload lacks direct student objects
  const studentsState = useSelector((state) => state.admin?.students || {});
  const globalStudents = studentsState.studentList || studentsState.students || [];

  /* =====================================================
     FILTER & MODAL STATES
  ====================================================== */
  const [activeFilter, setActiveFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState("review");
  const [saving, setSaving] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);

  /* =====================================================
     EVALUATION FORM LAYOUT STATE
  ====================================================== */
  const [formData, setFormData] = useState({
    submission_status: "Reviewed",
    admin_notes: ""
  });

  /* =====================================================
     FETCH LIFECYCLE MANAGEMENT
  ====================================================== */
  useEffect(() => {
    dispatch(fetchSubmissionsByAssignment({
      page: currentPage,
      limit,
      status: activeFilter !== "all" ? activeFilter : undefined
    }));
  }, [dispatch, currentPage, activeFilter]);

  useEffect(() => {
    // Hydrate student matrix data pool if empty
    if (globalStudents.length === 0) {
      dispatch(fetchStudents({ page: 1, limit: 1000, status: "active" }));
    }
  }, [dispatch, globalStudents.length]);

  /* =====================================================
     HANDLERS & WORKFLOW MODALS
  ====================================================== */
  const handlePageChange = (page) => { 
    if (page >= 1 && page <= totalPages) dispatch(setCurrentPage(page)); 
  };

  const handleFilterChange = (statusOption) => {
    setActiveFilter(statusOption);
    dispatch(setCurrentPage(1));
  };

  const openEvaluationModal = (submission) => {
    setSelectedSubmission(submission);
    setFormData({
      submission_status: submission.submission_status === "Pending" ? "Reviewed" : submission.submission_status,
      admin_notes: submission.admin_notes || ""
    });
    setActiveTab("review");
    setShowModal(true);
  };

  const handleSubmitEvaluation = async (e) => {
    e.preventDefault();
    if (!formData.submission_status) {
      toastr.warning("Please configure a target evaluation status scope.");
      return;
    }

    setSaving(true);

    const submissionPayload = {
      id: selectedSubmission.id,
      evaluationData: {
        submission_status: formData.submission_status,
        admin_notes: formData.admin_notes
      }
    };

    try {
      await dispatch(recordSubmissionEvaluation(submissionPayload)).unwrap();
      toastr.success("Submission profile evaluated and synchronized.");
      setShowModal(false);
      dispatch(fetchSubmissionsByAssignment({
        page: currentPage,
        limit,
        status: activeFilter !== "all" ? activeFilter : undefined
      }));
    } catch (err) {
      toastr.error(err || "Operational evaluation submission error anomaly.");
    } finally {
      setSaving(false);
    }
  };

  /* =====================================================
     RESOLUTIONS & CLEAN FORMATTERS
  ====================================================== */
  const getStudentObject = (studentId) => {
    return globalStudents.find((s) => Number(s.id) === Number(studentId));
  };

  const getStudentName = (submission) => {
    if (submission.student_name) return submission.student_name;
    if (submission.student?.name) return submission.student.name;
    
    const matched = getStudentObject(submission.student_id);
    return matched ? (matched.student_name || matched.name) : `Profile Reference ID #${submission.student_id}`;
  };

  const getAdmissionNumber = (submission) => {
    if (submission.admission_number) return submission.admission_number;
    if (submission.student?.admission_number) return submission.student.admission_number;

    const matched = getStudentObject(submission.student_id);
    return matched ? matched.admission_number : "Active Profile ID";
  };

  const getStatusPillClass = (status) => {
    switch (String(status).toLowerCase()) {
      case "reviewed": return "pill-green";
      case "submitted": return "pill-blue";
      case "pending": default: return "pill-amber";
    }
  };

  const formatDateDisplay = (dateString) => {
    if (!dateString) return "—";
    const dt = new Date(dateString);
    return isNaN(dt.getTime()) ? dateString : dt.toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" });
  };

  // Safe internal metric aggregation counts based on existing visible buffer rows
  const getKpiMetrics = (type) => {
    if (type === "pending") return submissions.filter(s => s.submission_status === "Pending").length;
    if (type === "reviewed") return submissions.filter(s => s.submission_status === "Reviewed").length;
    return submissions.length;
  };

  const filteredSubmissions = submissions.filter((sub) => 
    getStudentName(sub).toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(sub.id).includes(searchTerm)
  );

  return (
    <div className="hms-page">
      <div className="hms-wrap">

        {/* PAGE HEADER */}
        <div className="hms-header">
          <div>
            <p className="hms-eyebrow">Academy Operations · Admin Console</p>
            <h1 className="hms-title">Homework Submissions Desk</h1>
          </div>
        </div>

        {/* KPI METRICS STRIP */}
        <div className="hms-kpi-grid">
          {[
            { label: "Loaded Submissions Matrix", value: getKpiMetrics("all") },
            { label: "Pending Evaluations Stack", value: getKpiMetrics("pending") },
            { label: "Reviewed Tasks Roster", value: getKpiMetrics("reviewed") },
          ].map(({ label, value }) => (
            <div key={label} className="hms-kpi">
              <span className="hms-kpi-label">{label}</span>
              <span className="hms-kpi-val">{loading ? <Skeleton w={40} h={22} r={5} /> : value}</span>
            </div>
          ))}
        </div>

        {/* TOOLBAR & CONTROL FILTER MATRICES */}
        <div className="hms-toolbar">
          <div className="hms-search">
            <i className="fas fa-search hms-search-icon"></i>
            <input type="text" placeholder="Search by student roster title or ID..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
          </div>
          
          <div className="hms-filter-tabs">
            {[
              { key: "all", label: "All Scope" },
              { key: "pending", label: "Pending" },
              { key: "submitted", label: "Submitted" },
              { key: "reviewed", label: "Reviewed" },
            ].map(({ key, label }) => (
              <button 
                key={key} 
                type="button" 
                className={`hms-filter-btn${activeFilter === key ? " active" : ""}`}
                onClick={() => handleFilterChange(key)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* RESPONSIVE DATA TABLE CONTAINER */}
        <div className="hms-card">
          <div className="table-responsive">
            <table className="hms-table">
              <thead>
                <tr>
                  <th style={{ width: "15%" }}>Submission ID</th>
                  <th style={{ width: "30%" }}>Student Roster Identity</th>
                  <th style={{ width: "20%" }}>Lifecycle Status</th>
                  <th style={{ width: "20%" }}>Last Handshake Update</th>
                  <th className="text-end" style={{ width: "15%" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="hms-row">
                      <td><Skeleton w="50%" h={12} /></td>
                      <td><Skeleton w="80%" h={12} /><Skeleton w="40%" h={10} style={{ marginTop: 6 }} /></td>
                      <td><Skeleton w={70} h={22} r={20} /></td>
                      <td><Skeleton w={90} h={12} /></td>
                      <td style={{ textAlign: "right" }}><Skeleton w={65} h={30} r={7} style={{ marginLeft: "auto" }} /></td>
                    </tr>
                  ))
                ) : filteredSubmissions.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="hms-empty-state">
                      <div className="hms-empty-icon-wrap"><i className="fas fa-folder-open"></i></div>
                      <p className="hms-empty-title">No matching submission logs found</p>
                      <span className="hms-empty-desc">No records found within this filter context criteria parameters.</span>
                    </td>
                  </tr>
                ) : (
                  filteredSubmissions.map((sub) => (
                    <tr key={sub.id} className="hms-row">
                      <td data-label="Submission ID">
                        <span className="hms-id-badge">#{sub.id}</span>
                      </td>
                      <td data-label="Student Identity">
                        <div className="hms-name">{getStudentName(sub)}</div>
                        <div className="hms-subtext">{getAdmissionNumber(sub)}</div>
                      </td>
                      <td data-label="Lifecycle Status">
                        <span className={`hms-pill ${getStatusPillClass(sub.submission_status)}`}>
                          {sub.submission_status || "Pending"}
                        </span>
                      </td>
                      <td data-label="Last Update">
                        <div className="hms-date-main">{formatDateDisplay(sub.updated_at || sub.submitted_at)}</div>
                      </td>
                      <td className="text-end" data-label="Actions">
                        <button 
                          type="button" 
                          className="hms-btn-outline" 
                          onClick={() => openEvaluationModal(sub)}
                        >
                          Evaluate
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* PAGINATION PANEL */}
          <div className="hms-footer">
            <span className="hms-count">{filteredSubmissions.length} active row elements loaded</span>
            <div className="hms-pagination">
              <button 
                type="button" 
                className="hms-page-btn hms-page-arrow" 
                disabled={currentPage === 1 || loading} 
                onClick={() => handlePageChange(currentPage - 1)}
              >
                <i className="fas fa-chevron-left"></i>
              </button>
              
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((i) => (
                <button 
                  key={i} 
                  type="button" 
                  onClick={() => handlePageChange(i)} 
                  className={`hms-page-btn${currentPage === i ? " active" : ""}`}
                >
                  {i}
                </button>
              ))}

              <button 
                type="button" 
                className="hms-page-btn hms-page-arrow" 
                disabled={currentPage === totalPages || loading} 
                onClick={() => handlePageChange(currentPage + 1)}
              >
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>

      </div>

      {/* EVALUATION DIALOG OVERLAY CORE MODAL */}
      {showModal && selectedSubmission && (
        <div className="hms-modal-overlay" onClick={(e) => e.target === e.currentTarget && !saving && setShowModal(false)}>
          <div className="hms-modal">

            {/* Sidebar Navigation Matrix */}
            <div className="hms-modal-side">
              <div className="hms-modal-side-header">
                <p className="hms-modal-side-title">Evaluation Hub</p>
                <p className="hms-modal-side-sub">Submission #{selectedSubmission.id}</p>
              </div>
              <nav className="hms-modal-nav">
                {[
                  { key: "review", icon: "fas fa-check-circle", label: "Perform Review" },
                  { key: "meta", icon: "fas fa-info-circle", label: "Metadata Context" },
                ].map(({ key, icon, label }) => (
                  <button 
                    key={key} 
                    type="button" 
                    className={`hms-nav-item${activeTab === key ? " active" : ""}`} 
                    onClick={() => setActiveTab(key)} 
                    disabled={saving}
                  >
                    <i className={icon}></i>
                    <span>{label}</span>
                  </button>
                ))}
              </nav>
            </div>

            {/* Main Form Interface */}
            <form onSubmit={handleSubmitEvaluation} className="hms-modal-main">
              <div className="hms-modal-header">
                <div>
                  <h2 className="hms-modal-title">Evaluate Allocation Target</h2>
                  <p className="hms-modal-sub">Assign marks review matrices and structured admin notes directly.</p>
                </div>
                <button type="button" className="btn-close shadow-none" onClick={() => setShowModal(false)} disabled={saving}></button>
              </div>

              <div className="hms-modal-body">
                <div className="row g-3">

                  {/* TAB ONE: PERFORM REVIEW FORM */}
                  {activeTab === "review" && (
                    <>
                      <div className="col-12">
                        <label className="hms-label">Target Evaluation Status Allocation *</label>
                        <select 
                          className="hms-field" 
                          value={formData.submission_status} 
                          onChange={(e) => setFormData(prev => ({ ...prev, submission_status: e.target.value }))}
                        >
                          <option value="Pending">Pending Audit Status</option>
                          <option value="Submitted">Submitted (Under Grading)</option>
                          <option value="Reviewed">Reviewed & Approved</option>
                        </select>
                      </div>

                      <div className="col-12">
                        <label className="hms-label">Administrative Notes & Internal Logs</label>
                        <textarea 
                          className="hms-field" 
                          rows="6" 
                          style={{ resize: "none" }} 
                          placeholder="Provide performance grading details, system modifications, or audit logs..." 
                          value={formData.admin_notes} 
                          onChange={(e) => setFormData(prev => ({ ...prev, admin_notes: e.target.value }))}
                        ></textarea>
                      </div>
                    </>
                  )}

                  {/* TAB TWO: SUBMISSION METADATA ARCHIVE */}
                  {activeTab === "meta" && (
                    <div className="col-12">
                      <div className="hms-meta-container">
                        <div className="hms-meta-row">
                          <span className="hms-meta-lbl">Target Student Profile</span>
                          <span className="hms-meta-val">{getStudentName(selectedSubmission)}</span>
                        </div>
                        <div className="hms-meta-row">
                          <span className="hms-meta-lbl">System Identifier Token</span>
                          <span className="hms-meta-val">UUID-{(selectedSubmission.id)}</span>
                        </div>
                        <div className="hms-meta-row">
                          <span className="hms-meta-lbl">Initial Handshake Date</span>
                          <span className="hms-meta-val">{formatDateDisplay(selectedSubmission.created_at || selectedSubmission.submitted_at)}</span>
                        </div>
                        {selectedSubmission.submission_link && (
                          <div className="hms-meta-row" style={{ borderBottom: "none", paddingBottom: 0 }}>
                            <span className="hms-meta-lbl">External Target Resource</span>
                            <span className="hms-meta-val">
                              <a href={selectedSubmission.submission_link} target="_blank" rel="noreferrer" className="hms-link-url">
                                View File Attachments <i className="fas fa-external-link-alt" style={{ fontSize: 10 }}></i>
                              </a>
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                </div>
              </div>

              {/* ACTION HANDLERS */}
              <div className="hms-modal-footer">
                <button type="button" className="hms-btn-cancel" onClick={() => setShowModal(false)} disabled={saving}>Cancel</button>
                <button type="submit" className="hms-btn-primary hms-btn-submit" disabled={saving}>
                  {saving ? (
                    <>
                      <Spinner size={15} color="#ffffff" thickness={2} />
                      Syncing grading data...
                    </>
                  ) : (
                    "Commit Evaluation"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── DESIGN SYSTEM ELEMENTS ── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,400;0,14..32,500;0,14..32,600;1,14..32,400&display=swap');

        @keyframes hms-spin { to { transform: rotate(360deg); } }
        @keyframes hms-shimmer {
          0%   { background-position: -600px 0; }
          100% { background-position:  600px 0; }
        }
        @keyframes hms-modal-in {
          from { opacity: 0; transform: scale(0.97) translateY(10px); }
          to   { opacity: 1; transform: scale(1)    translateY(0);     }
        }
        @keyframes hms-overlay-in { from { opacity: 0; } to { opacity: 1; } }

        .hms-page {
          background: #f5f5f7;
          min-height: 100vh;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          color: #1d1d1f;
          font-size: 14px;
        }
        .hms-wrap { max-width: 1200px; margin: 0 auto; padding: 2rem 1.5rem; }

        .hms-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; flex-wrap: wrap; margin-bottom: 1.5rem; }
        .hms-eyebrow { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.07em; color: #aeaeb2; margin: 0 0 4px; }
        .hms-title { font-size: 1.35rem; font-weight: 600; color: #1d1d1f; letter-spacing: -0.022em; margin: 0; }

        .hms-kpi-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 1.25rem; }
        .hms-kpi { background: #fff; border: 1px solid #d2d2d7; border-radius: 12px; padding: 14px 18px; display: flex; flex-direction: column; gap: 5px; }
        .hms-kpi-label { font-size: 11px; color: #6e6e73; font-weight: 500; text-transform: uppercase; letter-spacing: 0.05em; }
        .hms-kpi-val { font-size: 1.5rem; font-weight: 600; color: #1d1d1f; letter-spacing: -0.025em; font-variant-numeric: tabular-nums; min-height: 1.8rem; display: flex; align-items: center; }

        .hms-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 1rem; flex-wrap: wrap; }
        .hms-search { flex: 1; min-width: 260px; position: relative; }
        .hms-search-icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #aeaeb2; font-size: 12px; }
        .hms-search input {
          width: 100%; background: #fff; border: 1px solid #d2d2d7; border-radius: 10px;
          padding: 9px 12px 9px 34px; font-size: 13px; color: #1d1d1f; outline: none;
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        .hms-search input:focus { border-color: #0071e3; box-shadow: 0 0 0 3px rgba(0,113,227,0.15); }

        .hms-filter-tabs { display: flex; background: #e8e8ed; border-radius: 10px; padding: 2px; gap: 2px; }
        .hms-filter-btn {
          border: none; background: transparent; padding: 7px 14px; font-size: 12.5px; font-weight: 500;
          color: #6e6e73; border-radius: 8px; cursor: pointer; transition: all 0.15s;
        }
        .hms-filter-btn:hover { color: #1d1d1f; }
        .hms-filter-btn.active { background: #fff; color: #1d1d1f; box-shadow: 0 1px 3px rgba(0,0,0,0.08); }

        .hms-btn-primary {
          background: #0071e3; color: #fff; border: none; border-radius: 10px; padding: 9px 18px;
          font-size: 13px; font-weight: 500; cursor: pointer; display: inline-flex; align-items: center; gap: 7px;
          transition: all 0.15s; white-space: nowrap; justify-content: center;
        }
        .hms-btn-primary:hover:not(:disabled) { background: #0077ed; }
        .hms-btn-primary:disabled { opacity: 0.65; cursor: not-allowed; }
        .hms-btn-submit { padding: 9px 22px; min-width: 158px; }

        .hms-card { background: #fff; border: 1px solid #d2d2d7; border-radius: 14px; overflow: hidden; }
        .hms-table { width: 100%; border-collapse: collapse; }
        .hms-table thead th {
          background: #f5f5f7; font-size: 10.5px; font-weight: 600; color: #6e6e73;
          text-transform: uppercase; letter-spacing: 0.07em; padding: 11px 18px; border-bottom: 1px solid #e8e8ed;
        }
        .hms-table tbody tr { border-bottom: 1px solid #f5f5f7; transition: background 0.1s; }
        .hms-row:hover { background: #fafafa; }
        .hms-table td { padding: 13px 18px; vertical-align: middle; text-align: left; }

        .hms-id-badge { background: #f5f5f7; border: 1px solid #d2d2d7; border-radius: 6px; padding: 2px 7px; font-size: 11.5px; font-weight: 600; color: #3c3c43; font-variant-numeric: tabular-nums; }
        .hms-name { font-size: 13.5px; font-weight: 600; color: #1d1d1f; }
        .hms-subtext { font-size: 11.5px; color: #8e8e93; margin-top: 2px; }
        
        .hms-pill { display: inline-block; padding: 3px 10px; border-radius: 20px; font-size: 11px; font-weight: 600; white-space: nowrap; text-transform: uppercase; letter-spacing: 0.03em; }
        .pill-amber { background: #fef3c7; color: #d97706; border: 1px solid #fde68a; }
        .pill-blue  { background: #dbeafe; color: #1e40af; border: 1px solid #bfdbfe; }
        .pill-green { background: #dcfce7; color: #15803d; border: 1px solid #bbf7d0; }

        .hms-date-main { font-size: 12.5px; font-weight: 500; color: #1d1d1f; font-variant-numeric: tabular-nums; }

        .hms-btn-outline {
          border: 1px solid #d2d2d7; background: #fff; border-radius: 8px; padding: 6px 14px;
          font-size: 12px; font-weight: 500; color: #3c3c43; cursor: pointer; transition: all 0.12s;
        }
        .hms-btn-outline:hover { border-color: #aeaeb2; background: #f5f5f7; color: #1d1d1f; }

        .hms-empty-state { text-align: center; padding: 3rem 1rem; }
        .hms-empty-icon-wrap { width: 52px; height: 52px; background: #f5f5f7; border-radius: 14px; display: inline-flex; align-items: center; justify-content: center; font-size: 1.4rem; color: #aeaeb2; margin-bottom: 12px; }
        .hms-empty-title { font-size: 14px; font-weight: 500; color: #3c3c43; margin: 0 0 4px; }
        .hms-empty-desc { font-size: 12px; color: #aeaeb2; }

        .hms-footer { display: flex; align-items: center; justify-content: space-between; padding: 12px 18px; border-top: 1px solid #f5f5f7; gap: 10px; flex-wrap: wrap; }
        .hms-count { font-size: 12px; color: #6e6e73; font-weight: 500; }
        .hms-pagination { display: flex; gap: 4px; }
        .hms-page-btn { min-width: 30px; height: 30px; padding: 0 6px; border: 1px solid #e8e8ed; background: #fff; border-radius: 7px; font-size: 12px; font-weight: 500; color: #3c3c43; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; transition: all 0.12s; }
        .hms-page-btn:hover:not(:disabled):not(.active) { background: #f5f5f7; border-color: #d2d2d7; }
        .hms-page-btn.active { background: #0071e3; color: #fff; border-color: #0071e3; }
        .hms-page-btn:disabled { opacity: 0.3; cursor: not-allowed; }

        .hms-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.36); backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; z-index: 9999; padding: 16px; animation: hms-overlay-in 0.2s ease; }
        .hms-modal { width: 100%; max-width: 780px; height: 460px; background: #fff; border-radius: 18px; border: 1px solid #d2d2d7; box-shadow: 0 32px 60px -12px rgba(0,0,0,0.18); display: flex; overflow: hidden; animation: hms-modal-in 0.24s cubic-bezier(0.34,1.1,0.64,1); }

        .hms-modal-side { width: 200px; background: #f5f5f7; border-right: 1px solid #e8e8ed; display: flex; flex-direction: column; flex-shrink: 0; }
        .hms-modal-side-header { padding: 18px 16px; border-bottom: 1px solid #e8e8ed; }
        .hms-modal-side-title { font-size: 13px; font-weight: 600; color: #1d1d1f; margin: 0; }
        .hms-modal-side-sub { font-size: 11px; color: #aeaeb2; margin: 3px 0 0; }
        
        .hms-modal-nav { padding: 8px; display: flex; flex-direction: column; gap: 2px; }
        .hms-nav-item { display: flex; align-items: center; gap: 9px; padding: 9px 12px; border-radius: 9px; border: none; background: transparent; font-size: 13px; color: #6e6e73; cursor: pointer; text-align: left; width: 100%; transition: all 0.12s; }
        .hms-nav-item i { font-size: 12.5px; width: 14px; text-align: center; }
        .hms-nav-item:hover:not(.active) { background: #ebebf0; color: #3c3c43; }
        .hms-nav-item.active { background: #fff; color: #0071e3; font-weight: 500; border: 1px solid #e8e8ed; }

        .hms-modal-main { flex: 1; display: flex; flex-direction: column; min-width: 0; }
        .hms-modal-header { padding: 18px 26px; border-bottom: 1px solid #f5f5f7; display: flex; align-items: flex-start; justify-content: space-between; }
        .hms-modal-title { font-size: 15px; font-weight: 600; color: #1d1d1f; margin: 0; }
        .hms-modal-sub { font-size: 12px; color: #6e6e73; margin: 3px 0 0; }
        .hms-modal-body { flex: 1; overflow-y: auto; padding: 22px 26px; }
        .hms-modal-footer { padding: 14px 26px; border-top: 1px solid #f5f5f7; display: flex; justify-content: flex-end; align-items: center; gap: 10px; background: #f5f5f7; }

        .hms-label { display: block; font-size: 11px; font-weight: 600; color: #6e6e73; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 5px; }
        .hms-field { width: 100%; border: 1px solid #d2d2d7; border-radius: 9px; padding: 9px 12px; font-size: 13px; color: #1d1d1f; background: #fff; outline: none; transition: all 0.15s; font-family: inherit; }
        .hms-field:focus { border-color: #0071e3; box-shadow: 0 0 0 3px rgba(0,113,227,0.15); }

        .hms-meta-container { background: #f5f5f7; border: 1px solid #d2d2d7; border-radius: 10px; padding: 4px 16px; }
        .hms-meta-row { display: flex; justify-content: space-between; align-items: center; padding: 12px 0; border-bottom: 1px solid #e8e8ed; gap: 10px; }
        .hms-meta-lbl { font-size: 12px; font-weight: 500; color: #6e6e73; }
        .hms-meta-val { font-size: 12.5px; font-weight: 600; color: #1d1d1f; text-align: right; }
        .hms-link-url { color: #0071e3; text-decoration: none; font-weight: 500; }
        .hms-link-url:hover { text-decoration: underline; }

        .hms-btn-cancel { border: 1px solid #d2d2d7; background: #fff; border-radius: 9px; padding: 9px 18px; font-size: 13px; font-weight: 500; color: #6e6e73; cursor: pointer; transition: all 0.12s; }
        .hms-btn-cancel:hover { background: #f5f5f7; color: #3c3c43; }

        @media (max-width: 991px) {
          .hms-kpi-grid { grid-template-columns: repeat(2, 1fr); }
        }

        @media (max-width: 768px) {
          .hms-wrap { padding: 1.25rem 1rem; }
          .hms-kpi-grid { grid-template-columns: 1fr; }
          .hms-toolbar { flex-direction: column; align-items: stretch; }
          .hms-search { width: 100%; min-width: 0; }
          .hms-filter-tabs { width: 100%; justify-content: space-between; }
          .hms-filter-btn { flex: 1; text-align: center; font-size: 11.5px; padding: 7px 4px; }
          .hms-table thead { display: none; }
          .hms-table tbody tr { display: block; border: 1px solid #e8e8ed; border-radius: 10px; margin-bottom: 12px; padding: 8px 14px; background: #fff; }
          .hms-table td { display: flex; justify-content: space-between; align-items: center; padding: 8px 0; border: none; width: 100% !important; text-align: left !important; }
          .hms-table td::before { content: attr(data-label); font-size: 11px; font-weight: 600; color: #8e8e93; text-transform: uppercase; letter-spacing: 0.03em; flex-shrink: 0; margin-right: 15px; display: inline-block; }
          .hms-table td.text-end, .hms-table td[data-label="Actions"] { justify-content: flex-end; }
          .hms-table td.text-end::before { display: none; }
          .hms-modal-overlay { padding: 0; }
          .hms-modal { flex-direction: column; height: 100vh; max-height: 100vh; width: 100vw; max-width: 100vw; border-radius: 0; border: none; animation: hms-overlay-in 0.2s ease; }
          .hms-modal-side { width: 100%; border-right: none; border-bottom: 1px solid #e8e8ed; }
          .hms-modal-side-header { display: none; }
          .hms-modal-nav { flex-direction: row; overflow-x: auto; padding: 8px; gap: 6px; }
          .hms-nav-item { white-space: nowrap; width: auto; font-size: 12.5px; padding: 8px 14px; }
          .hms-nav-item.active { border-color: #d2d2d7; background: #fff; }
          .hms-modal-body { padding: 16px; }
          .hms-modal-header { padding: 16px 20px; }
          .hms-modal-footer { padding: 12px 20px; background: #fff; border-top: 1px solid #e8e8ed; }
        }

        @media (max-width: 480px) {
          .hms-modal-footer { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
          .hms-btn-cancel, .hms-btn-submit { width: 100%; min-width: 0; margin: 0; text-align: center; }
          .hms-footer { flex-direction: column; align-items: stretch; text-align: center; }
          .hms-pagination { justify-content: center; }
        }
      `}</style>
    </div>
  );
};

export default HomeworkSubmissionList;